import { inject, Injectable } from '@angular/core';
import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { PharmacyViewModel } from '@services/models/pharmacy-view.model';
import { PharmaciesSearchService } from '@services/pharmacies.search.service';
import { Store } from '@services/store';
import { OpenSearchQuerySimpleResult } from '@shared/providers/opensearch-api';
import { intersection, sortBy } from 'lodash';
import { defer, firstValueFrom, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PharmaciesSearchStore extends Store<PharmacyViewModel> {
  private readonly searchService = inject(PharmaciesSearchService);

  protected getDataSource(key: string): Observable<any> {
    return this.searchService.getByNcpdp(key);
  }

  getByNpi(npi: string): Observable<PharmacyViewModel | undefined> {
    return this.searchService.getByNpi(npi);
  }

  getPharmacies(ncpdps: string[]): Observable<PharmacyViewModel[]> {
    return defer(async () => {
      const fromStore = await this.getFromStore(ncpdps);
      if (fromStore) return sortBy(Object.values(fromStore), 'name');
      return await this.loadPharmacies(ncpdps);
    });
  }

  private async getFromStore(ncpdps: string[]) {
    const keys = await this.keys();
    const compare = intersection(ncpdps, keys);
    if (compare.length === ncpdps.length) {
      return await this.getMany(ncpdps);
    }
  }

  private async loadPharmacies(ncpdps: string[]) {
    const query: OpenSearchRequestFilter[] = [
      {
        field: 'ncpdp',
        operator: 'in',
        value: ncpdps,
      },
    ];
    const data = await firstValueFrom(this.searchService.searchData(query, { from: 0, size: 10000 }, { name: 'asc' }), {
      defaultValue: { items: [], total: 0 } as OpenSearchQuerySimpleResult<PharmacyViewModel>,
    });
    await Promise.all(data.items.map(item => this.set(item.ncpdp, item)));
    return data.items;
  }
}
